/**
 * Mục "Lễ tân" của bản kê lương.
 *
 * Tách khỏi `WorkBreakdownDetail.tsx` vì tệp đó đã chạm trần 300 dòng của
 * lint — cùng lý do và cùng cách xử như `StudioAssistantDetail.tsx`, không
 * phải vì nội dung khác loại. Cái rào `hasOwnHoursSection` gác mục này nằm ở
 * `deptSections.ts`, cạnh rào của mục thợ — sửa một rào là thấy ngay rào kia.
 */

import type { PayrollRecord } from "../../lib";
import { AmountRow, AmountTable, DetailSection } from "./primitives";
import { hasOwnHoursSection } from "./deptSections";

export function ReceptionDetail({ record }: { record: PayrollRecord }) {
  if (hasOwnHoursSection(record.department)) return null;
  const b = record.upsaleBreakdown;
  const hours = record.hoursWorked ?? 0;
  const overtime = record.overtimeHours ?? 0;
  // CEO chốt 1a (18/09/2026): bản kê phải hiện TIỀN của số giờ thường, không
  // chỉ số giờ. Máy chủ gửi ô này từ lâu, chỉ là hợp đồng chưa khai nên giao
  // diện không đọc được (đã khai bù cùng ngày).
  //
  // Vẽ theo ">0" chứ không theo "có giờ": chỉ lễ tân mới có ô này (máy lương
  // chỉ gán `HourlyPay` ở lễ tân và trợ lý studio, mà trợ lý đã rẽ sang mục
  // riêng), nên hiện "Tiền công giờ thường: 0đ" cho phòng khác rơi vào đây là
  // bịa ra một khoản lương không tồn tại.
  const hourlyPay = record.hourlyPay ?? 0;
  // `hourlyPay` cũng phải nằm trong cái rào "không có gì thì thôi": số giờ trên
  // bản ghi là số ĐÃ LÀM TRÒN, nên một ca 0,4 giờ cho ra `hoursWorked = 0` mà
  // `hourlyPay > 0`. Bỏ nó khỏi rào là giấu mất một khoản tiền có thật.
  if (!b && !hours && !overtime && !hourlyPay) return null;

  // CEO chốt 1a (18/09/2026, lượt hai): sáu ô của `upsaleBreakdown` là SỐ LƯỢT
  // bán thêm, KHÔNG phải tiền — máy lương đếm lượt rồi mới nhân đơn giá ra
  // `commission`. Trước bản vá này chúng đi thẳng vào `AmountRow` dưới dạng
  // `number`, mà `AmountRow` gặp `number` thì LUÔN in qua `vnd()`: bán 7 lượt
  // hiện ra "7đ". Truyền chuỗi để thoát cái cửa đó.
  const rows = [
    { key: "at-cv", label: "Ảnh thẻ / CV", count: b?.atCv ?? 0 },
    { key: "concept-2", label: "Concept 2", count: b?.concept2 ?? 0 },
    { key: "trang-phuc", label: "Trang phục", count: b?.clothing ?? 0 },
    { key: "dn-1-buoi", label: "Doanh nghiệp 1 buổi", count: b?.dn1buoi ?? 0 },
    { key: "dn-1-ngay", label: "Doanh nghiệp 1 ngày", count: b?.dn1ngay ?? 0 },
  ].filter((r) => r.count !== 0);

  return (
    <DetailSection
      testId="detail-reception"
      icon="🏪"
      title="Lễ tân"
      // `countLabel` chứ KHÔNG phải `total`: `total` của `DetailSection` in qua
      // `vnd()`, mà số này là tổng SỐ LƯỢT. Cố ý KHÔNG thay bằng một số tiền
      // nào khác — mục này vẽ cho MỌI phòng không phải thợ và không phải trợ
      // lý (CSKH, kinh doanh, kế toán…), nên `record.commission` ở đây không
      // cùng nghĩa giữa các phòng; đặt nó lên tiêu đề là đổi một con số sai
      // lấy một con số sai khác.
      {...(b?.total ? { countLabel: `${b.total} lượt bán thêm` } : {})}
    >
      <AmountTable>
        {hours > 0 ? <AmountRow label="Giờ làm trong tháng" value={`${hours} giờ`} /> : null}
        {hourlyPay > 0 ? (
          <AmountRow
            testId="le-tan-tien-gio"
            label="Tiền công giờ thường"
            hint="Máy chủ tính sẵn — nhân tay số giờ ở trên với đơn giá có thể ra số khác, vì giờ lẻ và đơn giá riêng của từng ca đều đã tính vào đây"
            value={hourlyPay}
          />
        ) : null}
        {overtime > 0 ? (
          <AmountRow
            label="Giờ tăng ca"
            hint="Tính 150% đơn giá giờ thường"
            value={`${overtime} giờ`}
          />
        ) : null}
        {rows.map((r) => (
          <AmountRow
            key={r.key}
            testId={`le-tan-ban-them-${r.key}`}
            label={`Bán thêm — ${r.label}`}
            value={`${r.count} lượt`}
          />
        ))}
      </AmountTable>
    </DetailSection>
  );
}
